import React, { createContext, useState, useContext } from "react";
import {
    MONTHLY_PRICE_PLAN,
    ANNUAL_PRICE_AS_MONTHLY,
    ANNUAL_PRICE_AS_YEARLY
} from "core/constants";
import { getCsrfCookieName } from "helpers/gazetteGetCsrfCookieName";
import { getCookie } from "helpers/gazetteGetCookie";
import { useAuth } from "frontend/hooks/useAuth";

const Context = createContext();

export const CheckoutProvider = ({ children }) => {
    const [generalFormError, setGeneralFormError] = useState("");
    const [promoCodeValue, setPromoCodeValue] = useState("");
    const [promoCodeDetails, setPromoCodeDetails] = useState({
        discountType: "",
        amountOff: 0
    });
    const [receiptInfo, setReceiptInfo] = useState("");
    const [teamUsername, setTeamUsername] = useState("");

    const [subscriptionPrices, setSubscriptionPrices] = useState({
        yearly: ANNUAL_PRICE_AS_YEARLY,
        monthly: MONTHLY_PRICE_PLAN
    });

    const contextValues = {
        generalFormError,
        setGeneralFormError,
        promoCodeValue,
        setPromoCodeValue,
        promoCodeDetails,
        setPromoCodeDetails,
        subscriptionPrices,
        setSubscriptionPrices,
        receiptInfo,
        setReceiptInfo,
        teamUsername,
        setTeamUsername
    };

    return (
        <Context.Provider value={contextValues}>{children}</Context.Provider>
    );
};

export const useCheckoutFormGeneralError = () => {
    const context = useContext(Context);

    if (context === undefined) {
        throw new Error(
            "useCheckoutFormGeneralError must be used within a CheckoutProvider"
        );
    }

    return {
        generalFormError: context.generalFormError,
        setGeneralFormError: context.setGeneralFormError
    };
};

export const usePromoCode = () => {
    const context = useContext(Context);

    if (context === undefined) {
        throw new Error("usePromoCode must be used within a CheckoutProvider");
    }

    return {
        promoCodeValue: context.promoCodeValue,
        setPromoCodeValue: context.setPromoCodeValue,
        promoCodeDetails: context.promoCodeDetails,
        setPromoCodeDetails: context.setPromoCodeDetails
    };
};

export const useSubscriptionPrices = () => {
    const context = useContext(Context);

    if (context === undefined) {
        throw new Error(
            "useSubscriptionPrices must be used within a CheckoutProvider"
        );
    }

    return {
        subscriptionPrices: context.subscriptionPrices,
        setSubscriptionPrices: context.setSubscriptionPrices
    };
};

export const useUpdateBillingInfo = () => {
    const context = useContext(Context);

    if (context === undefined) {
        throw new Error(
            "useUpdateBillingInfo must be used within a CheckoutProvider"
        );
    }

    const { currentUser } = useAuth();

    const submitBillingInfo = async () => {
        // submitBillingInfo will always return true
        // we have to update VAT first or else it will
        // not show up on the invoice. We also do not want it to block
        // a purchase should this API ever be down. Thus we ensure it runs
        // first and no matter what return true
        let success = true;

        const { receiptInfo, teamUsername } = context;

        const usernameForAPI = teamUsername
            ? teamUsername
            : currentUser.username;

        // if the user did not enter any billing info, then skip
        // and move forward with transaction
        if (!receiptInfo) return success;

        const csrfCookieName = getCsrfCookieName();
        const csrftoken = getCookie({ name: csrfCookieName });

        const vatURL = `/${usernameForAPI}/receipt_info/update/`;
        const vatData = {
            receipt_info: receiptInfo
        };
        const vatOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": csrftoken
            },
            body: JSON.stringify(vatData)
        };

        await fetch(vatURL, vatOptions);

        return success;
    };

    return {
        submitBillingInfo,
        receiptInfo: context.receiptInfo,
        setReceiptInfo: context.setReceiptInfo,
        teamUsername: context.teamUsername,
        setTeamUsername: context.setTeamUsername
    };
};
